import React, { useState, useEffect, useMemo } from 'react';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { useAuth } from '../../hooks/useAuth';
import { useCalculateRates } from '../../hooks/useRate';
import { RateRequest, RateResponse } from '../../types/rate';
import { z } from 'zod';
import { Loader } from '../../components/ui/Loader';
import { usePincodeLookup } from '../../hooks/usePincodeLookup';
import toast from 'react-hot-toast';
import { useDrawer } from '../../hooks/useDrawer';
import { useNavigate } from 'react-router-dom';

/* ---------------- ZOD SCHEMA ---------------- */
const rateCalculatorSchema = z.object({
  pickupPincode: z.string().regex(/^\d{6}$/, 'Pincode must be 6 digits'),
  deliveryPincode: z.string().regex(/^\d{6}$/, 'Pincode must be 6 digits'),
  weight: z
    .string()
    .min(1, 'Weight is required')
    .refine(
      (val) => /^(0|[1-9]\d*)(\.\d+)?$/.test(val) && parseFloat(val) > 0,
      'Weight must be greater than 0'
    ),
  length: z
    .string()
    .min(1, 'Length is required')
    .refine(
      (val) => Number(val) > 0 && /^\d*(\.\d+)?$/.test(val),
      'Must be greater than 0'
    ),
  width: z
    .string()
    .min(1, 'Width is required')
    .refine(
      (val) => Number(val) > 0 && /^\d*(\.\d+)?$/.test(val),
      'Must be greater than 0'
    ),
  height: z
    .string()
    .min(1, 'Height is required')
    .refine(
      (val) => Number(val) > 0 && /^\d*(\.\d+)?$/.test(val),
      'Must be greater than 0'
    ),
  invoiceValue: z
    .string()
    .min(1, 'Invoice value is required')
    .refine(
      (val) => /^[1-9]\d*(\.\d+)?$/.test(val),
      'Invoice value must be a positive number'
    ),
});

type FormState = z.infer<typeof rateCalculatorSchema>;

interface ServiceRate {
  serviceId: number;
  serviceName: string;
  displayName: string;
  tat: number;
  weight: number;
  volumetricWeight?: number;
  zone?: string;
  dimensions?: string;
  totalCharge: number;
  baseRate?: number;
  fuel?: number;
  gst?: number;
  operationalStress?: boolean;
  baseWeightSlab: number;
}

export interface CourierRate {
  courierId: number;
  courierName: string;
  logoUrl: string;
  services: ServiceRate[];
}

export const RateCalculator: React.FC = () => {
  const { user } = useAuth();

  const {
    mutate: calculateRates,
    isPending: isLoading,
    isError,
    error,
    data: apiRates,
  } = useCalculateRates();

  const [shipmentType, setShipmentType] = useState<'forward' | 'return'>(
    'forward'
  );
  const [form, setForm] = useState<FormState>({
    pickupPincode: '',
    deliveryPincode: '',
    weight: '',
    length: '',
    width: '',
    height: '',
    invoiceValue: '',
  });
  const [paymentMode, setPaymentMode] = useState<'online' | 'COD'>('online');
  const [productType, setProductType] = useState<'Parcel' | 'Document'>(
    'Parcel'
  );
  const [deliveryMode, setDeliveryMode] = useState<'Both' | 'Surface' | 'Air'>(
    'Both'
  );
  const [insurance, setInsurance] = useState<boolean>(false);

  const [rates, setRates] = useState<CourierRate[]>([]);
  const { open: openDrawer, close: closeDrawer, Drawer } = useDrawer();
  const [ratesDrawerOpen, setRatesDrawerOpen] = useState(false);
  const navigate = useNavigate();

  const [pickupDetails, setPickupDetails] = useState<{ city: string; state: string } | null>(null);
  const [deliveryDetails, setDeliveryDetails] = useState<{ city: string; state: string } | null>(null);

  const flatRates = useMemo(() => {
    const list = rates.flatMap((courier) =>
      courier.services.map((service) => ({
        courierId: courier.courierId,
        courierName: courier.courierName,
        logoUrl: courier.logoUrl,
        ...service,
      }))
    );
    return list.sort((a, b) => a.totalCharge - b.totalCharge);
  }, [rates]);
  const [apiError, setApiError] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { mutate: lookupPincode } = usePincodeLookup();
  const [pincodeErrors, setPincodeErrors] = useState<{
    pickup?: string;
    delivery?: string;
  }>({});

  useEffect(() => {
    if (!user?.id) return;

    const timer = setTimeout(() => {
      // PICKUP VALIDATION
      if (form.pickupPincode.length === 6) {
        lookupPincode(form.pickupPincode, {
          onSuccess: (data) => {
            if (!data) {
              setPickupDetails(null);
              setPincodeErrors((prev) => ({
                ...prev,
                pickup: 'Invalid pickup pincode',
              }));
            } else {
              setPickupDetails({ city: data.District, state: data.State });
              setPincodeErrors((prev) => {
                const updated = { ...prev };
                delete updated.pickup;
                return updated;
              });
            }
          },
        });
      } else {
        setPickupDetails(null);
      }

      // DELIVERY VALIDATION
      if (form.deliveryPincode.length === 6) {
        lookupPincode(form.deliveryPincode, {
          onSuccess: (data) => {
            if (!data) {
              setDeliveryDetails(null);
              setPincodeErrors((prev) => ({
                ...prev,
                delivery: 'Invalid delivery pincode',
              }));
            } else {
              setDeliveryDetails({ city: data.District, state: data.State });
              setPincodeErrors((prev) => {
                const updated = { ...prev };
                delete updated.delivery;
                return updated;
              });
            }
          },
        });
      } else {
        setDeliveryDetails(null);
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [form.pickupPincode, form.deliveryPincode, user?.id]);

  // Convert API response to CourierRate format
  const convertApiResponseToCourierRates = (
    apiResponse: any
  ): CourierRate[] => {
    if (!apiResponse?.couriers) return [];

    return apiResponse.couriers.map((courier: any) => ({
      courierId: courier.courier_id,
      courierName: courier.name,
      logoUrl: courier.logo_url,
      services: courier.service_types.map((service: any) => {
        const rb = service.rate_breakup || {};

        const totalCharge = rb.total_charge;

        return {
          serviceId: service.service_id,
          serviceName: service.service_name,
          displayName: service.display_name,
          tat: service.expected_delivery_days,
          weight: service.rate_breakup?.base_weight_slab,
          totalCharge: service.rate_breakup?.total_charge,
        };
      }),
    }));
  };

  // Update rates when API response changes
  useEffect(() => {
    if (apiRates) {
      setRates(convertApiResponseToCourierRates(apiRates));
    }
  }, [apiRates]);

  useEffect(() => {
    if (ratesDrawerOpen) {
      openDrawer({
        position: 'right',
        className: 'w-full max-w-3xl bg-gray-50',
        closable: false, // We use our own close button in the header
        element: (
          <div className="flex flex-col h-full relative">
            <div className="p-6 bg-white border-b border-gray-100 flex justify-between items-center sticky top-0 z-10 shadow-sm">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Available Couriers</h2>
                <p className="text-sm text-gray-500">Select the best rate for your shipment</p>
              </div>
              <button
                onClick={() => {
                  setRatesDrawerOpen(false);
                  closeDrawer();
                }}
                className="text-gray-500 hover:text-gray-900 p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="p-6 grid grid-cols-2 gap-4">
              {isLoading && (
                <div className="col-span-2 flex flex-col items-center justify-center py-20 space-y-4">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
                  <p className="text-gray-500 font-medium">Fetching best rates...</p>
                </div>
              )}

              {!isLoading && flatRates.length === 0 && (
                <div className="col-span-2 text-center py-20 bg-white rounded-xl border border-gray-100 shadow-sm">
                  <div className="text-gray-400 mb-2 text-2xl">📦</div>
                  <p className="text-gray-600 font-medium">No couriers available for this route.</p>
                  <p className="text-sm text-gray-400 mt-1">Please try adjusting your dimensions or pincodes.</p>
                </div>
              )}

              {!isLoading && flatRates.map((rate) => (
                <div key={`${rate.courierId}-${rate.serviceId}`} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:border-primary/30 transition-colors cursor-pointer group flex flex-col h-full">
                  <div className="flex items-start justify-between mb-4 border-b border-gray-50 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-lg bg-gray-50 border border-gray-100 p-1 flex items-center justify-center shrink-0">
                        <img
                          src={rate.logoUrl}
                          alt={rate.courierName}
                          className="w-full h-full object-contain mix-blend-multiply"
                        />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 leading-tight group-hover:text-primary transition-colors">{rate.courierName}</h3>
                        <p className="text-xs text-gray-500">{rate.displayName}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-primary">₹{rate.totalCharge.toFixed(2)}</div>
                      <div className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Incl. GST</div>
                    </div>
                  </div>

                  <div className="mt-auto">
                    <div className="flex justify-between items-center text-sm mb-4">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-400 mb-0.5 uppercase tracking-wide font-semibold">Est. Delivery</span>
                        <span className="font-bold text-gray-700">{rate.tat} Days</span>
                      </div>
                      <div className="w-px h-8 bg-gray-100"></div>
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-400 mb-0.5 uppercase tracking-wide font-semibold">Weight</span>
                        <span className="font-bold text-gray-700">{rate.weight} Kg</span>
                      </div>
                    </div>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        closeDrawer();
                        navigate('/b2b/orders/add');
                      }}
                      className="w-full font-semibold"
                    >
                      Ship Now
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      });
    }
  }, [ratesDrawerOpen, flatRates, isLoading]);

  useEffect(() => {
    if (isError) {
      toast.error('Unable to fetch rate');
      setRates([]);
    }
  }, [isError]);

  // Prepare data for API call
  const prepareRateCalculationData = (): RateRequest => {
    return {
      pickup_pincode: form.pickupPincode,
      delivery_pincode: form.deliveryPincode,
      invoice_value: parseFloat(form.invoiceValue) || 100,
      payment_mode: paymentMode,
      product_type: productType,
      delivery_mode: deliveryMode,
      insurance: insurance,
      number_of_package: 1,
      package_details: [
        {
          no_of_items: 1,
          package_content: 'test',
          invoice_value: parseFloat(form.invoiceValue) || 100,
          package_dimension: {
            weight: parseFloat(form.weight) || 1,
            length: parseFloat(form.length as string),
            width: parseFloat(form.width as string),
            height: parseFloat(form.height as string),
          },
        },
      ],
    };
  };

  const calculateRatesHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');
    if (pincodeErrors.pickup || pincodeErrors.delivery) {
      toast.error('Please fix invalid pincodes before calculating rates.');
      return;
    }
    // Validate form using Zod
    const result = rateCalculatorSchema.safeParse(form);

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
          toast.error(err.message);
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setErrors({});

    if (!user?.id) {
      setApiError('User not found. Please login again.');
      return;
    }

    const requestData = prepareRateCalculationData();

    setRatesDrawerOpen(true);
    // Call the actual API
    calculateRates({
      userId: user.id,
      rateData: requestData,
    });
  };

  const handleFormChange = (field: keyof FormState, value: string) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleDimensionChange = (
    field: 'length' | 'width' | 'height',
    value: string
  ) => {
    if (value === '') {
      handleFormChange(field, value);
      return;
    }

    if (/^[1-9]\d*(\.\d+)?$/.test(value)) {
      handleFormChange(field, value);
    }
  };

  const chargeableWeight = form.weight || '1.0';

  return (
    <div className=" bg-background py-4 lg:py-6">
      <div className="mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl xl:text-3xl font-bold text-text mb-1">
            Rate Calculator
          </h1>
          <div className="flex items-center text-sm text-[#222222]">
            Calculate shipping rates instantly based on weight, zone, order type
            and service type.
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Left Column - Form */}
          <div className="xl:col-span-2">
            <div className="bg-card rounded-[16px] shadow-sm border py-10 px-10 h-full">
              <div className="p-0 border-b border-[#03030333] mb-8 pb-2">
                <h2 className="text-xl font-bold text-text mb-1">
                  Shipment Details
                </h2>
                <p className="text-sm font-normal text-text3">
                  Enter shipment details to check shipping charges.
                </p>
              </div>

              {/* Error Message */}
              {apiError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 text-sm">{apiError}</p>
                </div>
              )}

              <form onSubmit={calculateRatesHandler} className="space-y-2">
                {/* Shipment Type */}
                <div className="flex gap-6 items-center mb-3">
                  <label className="block text-base font-semibold text-text">
                    Shipment Type
                  </label>
                  <div className="flex gap-10">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="shipmentType"
                        value="forward"
                        checked={shipmentType === 'forward'}
                        onChange={(e) =>
                          setShipmentType(
                            e.target.value as 'forward' | 'return'
                          )
                        }
                        className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                      />
                      <span className="text-base text-text">Forward</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-not-allowed">
                      <input
                        type="radio"
                        name="shipmentType"
                        value="return"
                        disabled
                        checked={shipmentType === 'return'}
                        onChange={(e) =>
                          setShipmentType(
                            e.target.value as 'forward' | 'return'
                          )
                        }
                        className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                      />
                      <span className="text-base text-text">Return</span>
                    </label>
                  </div>
                </div>
                {/* Pincodes */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  <div>
                    <Input
                      label="Pickup Pin-code"
                      required
                      type="text"
                      placeholder="Enter pickup pincode"
                      value={form.pickupPincode}
                      error={errors.pickupPincode}
                      onChange={(e) =>
                        handleFormChange(
                          'pickupPincode',
                          e.target.value.replace(/\D/g, '').slice(0, 6)
                        )
                      }
                      maxLength={6}
                    />
                  </div>
                  <div>
                    <Input
                      label="Delivery Pin-code"
                      required
                      type="text"
                      placeholder="Enter delivery pincode"
                      value={form.deliveryPincode}
                      error={errors.deliveryPincode}
                      onChange={(e) =>
                        handleFormChange(
                          'deliveryPincode',
                          e.target.value.replace(/\D/g, '').slice(0, 6)
                        )
                      }
                      maxLength={6}
                    />
                  </div>
                </div>
                {/* Weight and Dimensions */}
                <div className="space-y-4">
                  <div className="grid grid-cols-1 xl:grid-cols-[1fr_2fr] gap-6">
                    <div>
                      <Input
                        label="Weight in (Kg)"
                        required
                        type="text"
                        placeholder="1"
                        value={form.weight}
                        error={errors.weight}
                        onChange={(e) => {
                          const value = e.target.value;

                          if (value === '') {
                            handleFormChange('weight', value);
                            return;
                          }

                          if (/^\d*(\.\d*)?$/.test(value)) {
                            handleFormChange('weight', value);
                          }
                        }}
                      />
                    </div>

                    <div>
                      <label className="block text-base font-medium text-gray-700 mb-2">
                        Dimension (cm) - L × W × H{' '}
                        <span className="text-[#F03434] ml-1">*</span>
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Input
                            type="text"
                            required
                            inputMode="decimal"
                            placeholder="Length"
                            value={form.length}
                            error={errors.length}
                            onChange={(e) =>
                              handleDimensionChange('length', e.target.value)
                            }
                            min="0"
                          />
                        </div>
                        <div>
                          <Input
                            type="text"
                            required
                            inputMode="decimal"
                            placeholder="Width"
                            value={form.width}
                            error={errors.width}
                            onChange={(e) =>
                              handleDimensionChange('width', e.target.value)
                            }
                            min="0"
                          />
                        </div>
                        <div>
                          <Input
                            type="text"
                            required
                            inputMode="decimal"
                            placeholder="Height"
                            value={form.height}
                            error={errors.height}
                            onChange={(e) =>
                              handleDimensionChange('height', e.target.value)
                            }
                            min="0"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Invoice Value */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  <div>
                    <Input
                      label="Invoice Value (₹)"
                      required
                      type="text"
                      placeholder="100"
                      value={form.invoiceValue}
                      error={errors.invoiceValue}
                      onChange={(e) => {
                        const value = e.target.value;

                        if (value === '') {
                          handleFormChange('invoiceValue', value);
                          return;
                        }

                        if (/^[1-9]\d*(\.\d+)?$/.test(value)) {
                          handleFormChange('invoiceValue', value);
                        }
                      }}
                    />
                  </div>
                </div>
                {/* Chargeable Weight Display */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Chargeable Weight (Kg)
                  </label>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    {chargeableWeight} Kg
                  </div>
                </div>
                {/* Payment Mode */}
                <div>
                  <label className="block text-base font-semibold text-text mb-3">
                    Order Type
                  </label>
                  <div className="flex gap-16">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="paymentMode"
                        value="online"
                        checked={paymentMode === 'online'}
                        onChange={(e) =>
                          setPaymentMode(e.target.value as 'online' | 'COD')
                        }
                        className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                      />
                      <span className="text-base text-text">Online</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="paymentMode"
                        value="COD"
                        checked={paymentMode === 'COD'}
                        onChange={(e) =>
                          setPaymentMode(e.target.value as 'online' | 'COD')
                        }
                        className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                      />
                      <span className="text-base text-text">COD</span>
                    </label>
                  </div>
                </div>
                {/* Insurance */}
                <div className="flex items-center gap-3 cursor-not-allowed">
                  <input
                    type="checkbox"
                    id="insurance"
                    checked={insurance}
                    disabled
                    onChange={(e) => setInsurance(e.target.checked)}
                    className="h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary"
                  />
                  <label
                    htmlFor="insurance"
                    className="text-base font-semibold text-text"
                  >
                    Insure Your Shipment
                  </label>
                </div>
                {/* Calculate Button */}
                <div className="flex justify-center">
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="py-3 text-lg"
                  >
                    {isLoading ? 'Calculating...' : 'Calculate Rates'}
                  </Button>
                </div>
              </form>
            </div>
          </div>

          {/* Right Column - Results (Empty State) */}
          <div className="xl:col-span-1 relative overflow-hidden rounded-[16px] ">
            {/* Background Image */}
            <div className="absolute inset-0 opacity-[0.15] z-0 pointer-events-none" style={{ backgroundImage: "url('/images/shipping_bg.png')", backgroundSize: 'cover', backgroundPosition: 'center' }}></div>

            <div className="h-full flex flex-col justify-center items-center py-10 lg:px-6 relative z-10">
              <h3 className="text-xl font-bold text-gray-900 mb-12">Check prices before you ship</h3>

              {/* Nodes Design */}
              <div className="relative w-full max-w-sm mx-auto mb-16 mt-8">
                {/* Curved Wavy Dashed Vector Line */}
                <div className="absolute top-1/2 left-0 right-0 h-32 -z-10 -translate-y-1/2 flex items-center justify-center">
                  <svg width="100%" height="100%" viewBox="0 0 400 120" preserveAspectRatio="none" className="overflow-visible">
                    <path
                      d="M 50,80 C 120,80 150,20 200,50 C 250,80 280,20 350,20"
                      fill="none"
                      stroke="#3B82F6"
                      strokeWidth="2.5"
                      strokeDasharray="6 6"
                      className="opacity-70"
                    />
                    <circle cx="50" cy="80" r="5" fill="#3B82F6" />
                    <circle cx="200" cy="50" r="4" fill="#3B82F6" opacity="0.6" />
                    <circle cx="350" cy="20" r="5" fill="#3B82F6" />
                  </svg>
                </div>

                <div className="flex justify-between items-center w-full relative h-32">
                  {/* Pickup Node (Bottom Left) */}
                  <div className="bg-white rounded-xl shadow-md border border-gray-100 p-4 w-36 z-10 flex flex-col items-start absolute left-0 bottom-0 transform -translate-y-2">
                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Pickup</div>
                    <div className="font-bold text-gray-900 text-lg">{form.pickupPincode || '------'}</div>
                    {pickupDetails ? (
                      <div className="text-xs text-[#059669] font-medium mt-1 leading-tight text-left">
                        {pickupDetails.city}, {pickupDetails.state}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-300 mt-1">Enter pincode</div>
                    )}
                  </div>

                  {/* Delivery Node (Top Right) */}
                  <div className="bg-white rounded-xl shadow-md border border-gray-100 p-4 w-36 z-10 flex flex-col items-start absolute right-0 top-0 transform translate-y-2">
                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Delivery</div>
                    <div className="font-bold text-gray-900 text-lg">{form.deliveryPincode || '------'}</div>
                    {deliveryDetails ? (
                      <div className="text-xs text-[#059669] font-medium mt-1 leading-tight text-left">
                        {deliveryDetails.city}, {deliveryDetails.state}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-300 mt-1">Enter pincode</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Multiple Courier Partners */}
              <div className="text-center mb-10">
                <h4 className="text-sm font-semibold text-gray-900 mb-4">Multiple courier partners</h4>
                <div className="flex justify-center -space-x-4 mb-6">
                  <img src="/brandLogos/delhiverynew size.jpg" alt="Delhivery" className="w-20 h-20 rounded-full border-[5px] border-white shadow-md object-cover bg-white" />
                  <img src="/brandLogos/xpressbeesnew size.jpg" alt="Xpressbees" className="w-20 h-20 rounded-full border-[5px] border-white shadow-md object-cover bg-white" />
                  <img src="/brandLogos/blue dartnew size.jpg" alt="Blue Dart" className="w-20 h-20 rounded-full border-[5px] border-white shadow-md object-cover bg-white" />
                  <img src="/brandLogos/shadowfaxnew size.jpg" alt="Shadowfax" className="w-20 h-20 rounded-full border-[5px] border-white shadow-md object-cover bg-white" />
                </div>
              </div>

              {/* Benefits */}
              <div className="flex flex-wrap justify-center gap-4 text-sm font-semibold text-gray-600">
                <span className="flex items-center gap-1">⭐ Best price guaranteed</span>
                <span className="flex items-center gap-1">⭐ On time delivery</span>
                <span className="flex items-center gap-1">⭐ 10,000+ pincodes</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Drawer />
    </div>
  );
};

export default RateCalculator;
